/**
 * Kadi Rose — site interactions.
 *
 * A direct port of the behaviour that lived in the React components. Everything
 * here is progressive enhancement: the markup renders and reads correctly with
 * this file absent, and each initialiser bails quietly when its element isn't on
 * the page.
 *
 * Header, menu overlay, reveal-on-scroll, the services list, the FAQ accordion,
 * the before/after wipe and the room carousel. Nothing else.
 */
(function () {
  "use strict";

  /* ---------------------------------------------------------------- header */

  function initHeader() {
    var header = document.querySelector("[data-site-header]");
    if (!header) return;

    /* The header carries `bg-bone` in the markup and keeps it at all times.
       An earlier version swapped in `bg-transparent` whenever the menu was
       open, which let the page behind show straight through the bar — the
       wordmark ended up sitting on top of whatever text happened to be there.
       Only the bottom rule changes now. */
    function sync() {
      var scrolled = window.scrollY > 8;
      header.classList.toggle("border-line", scrolled);
      header.classList.toggle("border-transparent", !scrolled);
    }

    sync();
    window.addEventListener("scroll", sync, { passive: true });
    document.addEventListener("kr:menu", sync);
  }

  /* ------------------------------------------------------------ menu overlay */

  /**
   * Focus moves in and returns, Escape closes, and the page behind cannot
   * scroll — the three things a modal has to get right.
   */
  function initMenu() {
    var overlay = document.querySelector("[data-menu-overlay]");
    var toggle = document.querySelector("[data-menu-toggle]");
    if (!overlay || !toggle) return;

    var label = toggle.querySelector("[data-menu-label]");
    var top = toggle.querySelector('[data-menu-bar="top"]');
    var middle = toggle.querySelector('[data-menu-bar="middle"]');
    var bottom = toggle.querySelector('[data-menu-bar="bottom"]');
    var items = overlay.querySelectorAll("[data-menu-item]");
    var restoreFocus = null;
    var open = false;

    function setOpen(next) {
      open = next;
      toggle.setAttribute("aria-expanded", String(next));
      toggle.setAttribute("aria-label", next ? "Close menu" : "Open menu");
      if (label) label.textContent = next ? "Close" : "Menu";

      if (top) {
        top.classList.toggle("top-0", !next);
        top.classList.toggle("top-1/2", next);
        top.classList.toggle("rotate-45", next);
      }
      if (middle) {
        middle.classList.toggle("opacity-0", next);
      }
      if (bottom) {
        bottom.classList.toggle("top-full", !next);
        bottom.classList.toggle("top-1/2", next);
        bottom.classList.toggle("-rotate-45", next);
      }

      if (next) {
        restoreFocus = document.activeElement;
        overlay.hidden = false;
        document.documentElement.setAttribute("data-menu-open", "");

        var scrollbar = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = "hidden";
        if (scrollbar > 0) document.body.style.paddingRight = scrollbar + "px";

        /* Next frame, so the transition has a start state to animate from. */
        requestAnimationFrame(function () {
          overlay.classList.remove("pointer-events-none", "opacity-0");
          overlay.classList.add("opacity-100");
          items.forEach(function (item) {
            item.removeAttribute("tabindex");
            item.style.transitionDelay = item.style.getPropertyValue("--menu-delay") || "";
            item.style.transitionDelay = getComputedStyle(item).getPropertyValue("--menu-delay");
            item.classList.remove("translate-y-3", "opacity-0");
          });
        });

        var first = overlay.querySelector("a, button");
        if (first) first.focus();
      } else {
        document.documentElement.removeAttribute("data-menu-open");
        overlay.classList.add("pointer-events-none", "opacity-0");
        overlay.classList.remove("opacity-100");
        items.forEach(function (item) {
          item.setAttribute("tabindex", "-1");
          item.style.transitionDelay = "0ms";
          item.classList.add("translate-y-3", "opacity-0");
        });

        document.body.style.overflow = "";
        document.body.style.paddingRight = "";

        window.setTimeout(function () {
          if (!open) overlay.hidden = true;
        }, 300);

        if (restoreFocus && restoreFocus.focus) restoreFocus.focus();
      }

      document.dispatchEvent(new CustomEvent("kr:menu"));
    }

    toggle.addEventListener("click", function () {
      setOpen(!open);
    });

    overlay.addEventListener("click", function (event) {
      if (event.target.closest("[data-menu-item]")) setOpen(false);
    });

    document.addEventListener("keydown", function (event) {
      if (!open) return;

      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;

      var focusable = overlay.querySelectorAll("a[href], button:not([disabled])");
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------------------------------------------------------------- reveal */

  /** Fades content up a few pixels as it enters the viewport, once. */
  function initReveal() {
    var nodes = document.querySelectorAll(".reveal");
    if (!nodes.length) return;

    if (typeof IntersectionObserver === "undefined") {
      nodes.forEach(function (node) {
        node.setAttribute("data-revealed", "true");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute("data-revealed", "true");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );

    nodes.forEach(function (node) {
      observer.observe(node);
    });
  }

  /* -------------------------------------------------------------- services */

  /**
   * Two independent behaviours on the services list.
   *
   * The rows open independently rather than one-at-a-time: someone comparing
   * standard against deep wants both lists on screen, which a single-open
   * accordion would prevent.
   *
   * "View all services" reveals the rest. Collapsing also closes any row that
   * was opened inside the revealed set and returns the page to the top of the
   * section — otherwise the ground moves under the reader and they end up
   * somewhere below the footer.
   */
  function initServices() {
    var root = document.querySelector("[data-services]");
    if (!root || root.dataset.servicesReady === "true") return;
    root.dataset.servicesReady = "true";

    function setRowOpen(trigger, open) {
      var panel = document.getElementById(trigger.getAttribute("aria-controls"));
      var chevron = trigger.querySelector("[data-service-chevron]");
      var copy = panel && panel.querySelector("[data-service-copy]");

      trigger.setAttribute("aria-expanded", String(open));
      if (panel) {
        panel.classList.toggle("grid-rows-[1fr]", open);
        panel.classList.toggle("grid-rows-[0fr]", !open);
      }
      if (copy) {
        copy.classList.toggle("opacity-100", open);
        copy.classList.toggle("opacity-0", !open);
      }
      if (chevron) chevron.classList.toggle("rotate-180", open);
    }

    var triggers = Array.prototype.slice.call(
      root.querySelectorAll("[data-service-trigger]")
    );

    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function () {
        setRowOpen(trigger, trigger.getAttribute("aria-expanded") !== "true");
      });
    });

  }

  /* ------------------------------------------------------------- accordion */

  /** Single-open accordion. Everything starts closed. */
  function initAccordions() {
    document.querySelectorAll("[data-accordion]").forEach(function (root) {
      var triggers = Array.prototype.slice.call(
        root.querySelectorAll("[data-accordion-trigger]")
      );

      function setOpen(trigger, open) {
        var panel = document.getElementById(trigger.getAttribute("aria-controls"));
        var chevron = trigger.querySelector("[data-accordion-chevron]");
        var answer = panel && panel.querySelector("[data-accordion-answer]");

        trigger.setAttribute("aria-expanded", String(open));
        if (panel) {
          panel.classList.toggle("grid-rows-[1fr]", open);
          panel.classList.toggle("grid-rows-[0fr]", !open);
        }
        if (answer) {
          answer.classList.toggle("opacity-100", open);
          answer.classList.toggle("opacity-0", !open);
        }
        if (chevron) chevron.classList.toggle("rotate-180", open);
      }

      triggers.forEach(function (trigger) {
        trigger.addEventListener("click", function () {
          var isOpen = trigger.getAttribute("aria-expanded") === "true";
          triggers.forEach(function (other) {
            setOpen(other, false);
          });
          if (!isOpen) setOpen(trigger, true);
        });
      });
    });
  }

  /* ------------------------------------------------------ comparison slider */

  /**
   * Only the 44px grip is draggable. Everywhere else in the frame a sideways
   * drag belongs to the carousel these sit inside, so the two horizontal
   * gestures never collide.
   */
  function initComparisons() {
    document.querySelectorAll("[data-comparison]").forEach(function (figure) {
      if (figure.dataset.comparisonReady === "true") return;
      figure.dataset.comparisonReady = "true";

      var frame = figure.querySelector("[data-comparison-frame]");
      var before = figure.querySelector("[data-comparison-before]");
      var divider = figure.querySelector("[data-comparison-divider]");
      var grip = figure.querySelector("[data-comparison-grip]");
      var input = figure.querySelector("[data-comparison-input]");
      var left = figure.querySelector('[data-comparison-corner="left"]');
      var right = figure.querySelector('[data-comparison-corner="right"]');
      if (!frame || !before || !divider || !grip || !input) return;

      var dragging = false;

      function apply(percent) {
        var value = Math.min(100, Math.max(0, percent));
        var rounded = Math.round(value);

        before.style.clipPath = "inset(0 " + (100 - value) + "% 0 0)";
        divider.style.left = value + "%";
        input.value = String(rounded);
        input.setAttribute(
          "aria-valuetext",
          rounded + "% before, " + (100 - rounded) + "% after"
        );

        if (left) {
          left.classList.toggle("opacity-100", value > 12);
          left.classList.toggle("opacity-0", value <= 12);
        }
        if (right) {
          right.classList.toggle("opacity-100", value < 88);
          right.classList.toggle("opacity-0", value >= 88);
        }
      }

      function fromClientX(clientX) {
        var rect = frame.getBoundingClientRect();
        apply(((clientX - rect.left) / rect.width) * 100);
      }

      grip.addEventListener("pointerdown", function (event) {
        dragging = true;
        grip.setPointerCapture(event.pointerId);
        fromClientX(event.clientX);
      });

      grip.addEventListener("pointermove", function (event) {
        if (dragging) fromClientX(event.clientX);
      });

      function endDrag(event) {
        dragging = false;
        if (grip.hasPointerCapture(event.pointerId)) grip.releasePointerCapture(event.pointerId);
      }

      grip.addEventListener("pointerup", endDrag);
      grip.addEventListener("pointercancel", endDrag);

      input.addEventListener("input", function () {
        apply(Number(input.value));
      });

      apply(50);
    });
  }

  /* ------------------------------------------------------------- carousels */

  /**
   * Native scroll-snap does the paging; the dots only report and set position.
   */
  function initCarousels() {
    document.querySelectorAll("[data-carousel]").forEach(function (track) {
      var name = track.getAttribute("data-carousel");
      var gap = Number(track.getAttribute("data-carousel-gap") || 0);
      var dots = document.querySelector('[data-carousel-dots="' + name + '"]');
      if (!dots) return;

      if (track.dataset.carouselReady === "true") return;
      track.dataset.carouselReady = "true";

      var buttons = Array.prototype.slice.call(dots.querySelectorAll("[data-carousel-dot]"));

      function stride() {
        var slide = track.firstElementChild;
        return slide ? slide.offsetWidth + gap : 0;
      }

      function sync() {
        var step = stride();
        if (!step) return;

        var index = Math.min(buttons.length - 1, Math.round(track.scrollLeft / step));

        buttons.forEach(function (button, i) {
          var active = i === index;
          button.setAttribute("aria-current", String(active));
          button.classList.toggle("bg-ink", active);
          button.classList.toggle("w-5", active);
          button.classList.toggle("w-1.5", !active);
          button.classList.toggle("bg-line-strong", !active);
          button.classList.toggle("hover:bg-ink-muted", !active);
        });
      }

      function bindDots() {
        buttons = Array.prototype.slice.call(dots.querySelectorAll("[data-carousel-dot]"));
        buttons.forEach(function (button, i) {
          if (button.dataset.bound === "true") return;
          button.dataset.bound = "true";
          button.addEventListener("click", function () {
            track.scrollTo({ left: i * stride(), behavior: "smooth" });
          });
        });
      }

      bindDots();
      track.addEventListener("scroll", sync, { passive: true });
      window.addEventListener("resize", sync);
      sync();

      /* Adding a card has to re-read the dots, which are rebuilt by whoever
         inserted it. Exposed rather than inferred so there is one entry point. */
      carousels[name] = {
        track: track,
        dots: dots,
        refresh: function () {
          bindDots();
          sync();
        },
      };
    });
  }

  /* Carousels by name, so a section can look one up after inserting a slide. */
  var carousels = {};

  /* ---------------------------------------------------- get started wizard */

  /**
   * Five panels over one Shopify contact form.
   *
   * Only the presentation is staged — there is a single form and a single
   * submit, so Shopify's own handling is untouched. Everything below bails if
   * the markup isn't on the page, and every panel is visible in the markup by
   * default, so a visitor without JavaScript sees one long form that still
   * posts rather than a dead first step.
   */
  /**
   * The preferred-dates picker.
   *
   * Built from `new Date()` rather than from any data, so it can never go
   * stale. Any number of dates can be chosen and each one toggles; the set is
   * held as ISO strings and written to a hidden input as readable text, which
   * is what lands in the notification email.
   *
   * Days before today are disabled rather than removed, so the grid keeps its
   * shape and the month reads normally.
   */
  function initCalendar(scope) {
    var root = scope.querySelector("[data-calendar]");
    if (!root) return;

    var grid = root.querySelector("[data-calendar-grid]");
    var title = root.querySelector("[data-calendar-title]");
    var prev = root.querySelector("[data-calendar-prev]");
    var next = root.querySelector("[data-calendar-next]");
    var value = scope.querySelector("[data-calendar-value]");
    var readout = scope.querySelector("[data-calendar-readout]");
    if (!grid || !title) return;

    var MONTHS = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var view = new Date(today.getFullYear(), today.getMonth(), 1);
    var picked = [];

    function iso(date) {
      var month = String(date.getMonth() + 1).padStart(2, "0");
      var day = String(date.getDate()).padStart(2, "0");
      return date.getFullYear() + "-" + month + "-" + day;
    }

    function label(key) {
      var parts = key.split("-");
      var date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return MONTHS[date.getMonth()].slice(0, 3) + " " + date.getDate();
    }

    function sync() {
      picked.sort();
      if (value) value.value = picked.map(label).join(", ");
      if (readout) {
        readout.textContent = picked.length
          ? picked.length + (picked.length === 1 ? " date chosen" : " dates chosen")
          : "";
      }
    }

    function render() {
      title.textContent = MONTHS[view.getMonth()] + " " + view.getFullYear();

      /* Never let anyone page back past the current month. */
      if (prev) {
        prev.disabled =
          view.getFullYear() === today.getFullYear() && view.getMonth() === today.getMonth();
      }

      grid.innerHTML = "";

      var firstWeekday = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
      var daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

      for (var blank = 0; blank < firstWeekday; blank += 1) {
        grid.appendChild(document.createElement("span"));
      }

      for (var day = 1; day <= daysInMonth; day += 1) {
        var date = new Date(view.getFullYear(), view.getMonth(), day);
        var key = iso(date);
        var past = date < today;
        var chosen = picked.indexOf(key) !== -1;

        var cell = document.createElement("button");
        cell.type = "button";
        cell.textContent = String(day);
        cell.setAttribute("data-date", key);
        cell.setAttribute("aria-pressed", chosen ? "true" : "false");
        cell.disabled = past;
        cell.className =
          "tnum mx-auto grid size-11 place-items-center rounded-full text-[1rem] transition-colors " +
          (past
            ? "text-brown/45 line-through"
            : chosen
              ? "bg-ink text-white"
              : "text-ink hover:bg-bone-deep");

        grid.appendChild(cell);
      }
    }

    grid.addEventListener("click", function (event) {
      var cell = event.target.closest("[data-date]");
      if (!cell || cell.disabled) return;
      var key = cell.getAttribute("data-date");
      var at = picked.indexOf(key);
      if (at === -1) picked.push(key);
      else picked.splice(at, 1);
      sync();
      render();
    });

    if (prev) {
      prev.addEventListener("click", function () {
        view = new Date(view.getFullYear(), view.getMonth() - 1, 1);
        render();
      });
    }
    if (next) {
      next.addEventListener("click", function () {
        view = new Date(view.getFullYear(), view.getMonth() + 1, 1);
        render();
      });
    }

    sync();
    render();
  }

  function initWizard() {
    var form = document.querySelector("[data-wizard]");
    if (!form || form.dataset.wizardReady === "true") return;
    form.dataset.wizardReady = "true";

    var panels = Array.prototype.slice.call(form.querySelectorAll("[data-wizard-step]"));
    if (!panels.length) return;

    var next = form.querySelector("[data-wizard-next]");
    var back = form.querySelector("[data-wizard-back]");
    var submit = form.querySelector("[data-wizard-submit]");
    var current = form.querySelector("[data-wizard-current]");
    var bar = form.querySelector("[data-wizard-bar]");
    var summary = form.querySelector("[data-wizard-summary]");
    var total = panels.length;
    var index = 0;

    /* -------------------------------------------------------- the steppers */

    form.querySelectorAll("[data-stepper]").forEach(function (stepper) {
      var input = stepper.querySelector("[data-stepper-input]");
      if (!input) return;

      function nudge(by) {
        var min = Number(input.min || 0);
        var max = Number(input.max || 99);
        var value = Number(input.value || 0) + by;
        input.value = String(Math.min(max, Math.max(min, value)));
      }

      var down = stepper.querySelector("[data-stepper-down]");
      var up = stepper.querySelector("[data-stepper-up]");
      if (down) down.addEventListener("click", function () { nudge(-1); });
      if (up) up.addEventListener("click", function () { nudge(1); });
    });

    /* ------------------------------------------- services → one hidden field */

    var choices = Array.prototype.slice.call(form.querySelectorAll("[data-service-choice]"));
    var servicesValue = form.querySelector("[data-services-value]");

    function syncServices() {
      if (!servicesValue) return;
      servicesValue.value = choices
        .filter(function (box) { return box.checked; })
        .map(function (box) { return box.value; })
        .join(", ");
    }

    choices.forEach(function (box) {
      box.addEventListener("change", syncServices);
    });

    /* ------------------------------------------ pets → reveal the follow-up */

    var petsDetail = form.querySelector("[data-pets-detail]");
    var petTypes = Array.prototype.slice.call(form.querySelectorAll("[data-pet-type]"));
    var petTypesValue = form.querySelector("[data-pet-types-value]");

    function syncPetTypes() {
      if (!petTypesValue) return;
      petTypesValue.value = petTypes
        .filter(function (box) { return box.checked; })
        .map(function (box) { return box.value; })
        .join(", ");
    }

    function syncPets() {
      if (!petsDetail) return;
      var picked = form.querySelector("[data-pets-choice]:checked");
      var hasPets = picked && picked.value === "Yes";
      petsDetail.hidden = !hasPets;
      /* A changed mind should not leave a stale answer behind. */
      if (!hasPets) {
        petTypes.forEach(function (box) { box.checked = false; });
        syncPetTypes();
      }
    }

    form.querySelectorAll("[data-pets-choice]").forEach(function (radio) {
      radio.addEventListener("change", syncPets);
    });
    petTypes.forEach(function (box) {
      box.addEventListener("change", syncPetTypes);
    });
    syncPets();

    /* ------------------------------------------------ the date picker */

    initCalendar(form);

    /* ------------------------------------------------------------- review */

    /** Reads every labelled field and prints what was actually filled in. */
    function buildSummary() {
      if (!summary) return;
      summary.innerHTML = "";

      form.querySelectorAll("[data-wizard-field]").forEach(function (field) {
        var value = (field.value || "").trim();
        if (!value) return;

        var panel = field.closest("[data-wizard-step]");
        var step = panel ? panel.getAttribute("data-wizard-step") : "1";

        var row = document.createElement("div");
        row.className =
          "flex items-baseline justify-between gap-4 border-b border-line py-3";

        var term = document.createElement("dt");
        term.className = "shrink-0 text-[0.9375rem] text-brown";
        term.textContent = field.getAttribute("data-wizard-field");

        var detail = document.createElement("dd");
        detail.className = "text-right text-[1rem] text-ink";
        /* Customer-entered text, set as text and never as markup. */
        detail.textContent = value;

        var edit = document.createElement("button");
        edit.type = "button";
        edit.className =
          "ml-3 shrink-0 text-[0.8125rem] uppercase tracking-[0.18em] text-brown underline transition-opacity hover:opacity-60";
        edit.textContent = "Edit";
        edit.addEventListener("click", function () {
          show(Number(step) - 1);
        });

        detail.appendChild(edit);
        row.appendChild(term);
        row.appendChild(detail);
        summary.appendChild(row);
      });

      if (!summary.children.length) {
        var empty = document.createElement("p");
        empty.className = "py-3 text-[1rem] text-brown";
        empty.textContent = "Nothing filled in yet — step back and add your details.";
        summary.appendChild(empty);
      }
    }

    /* --------------------------------------------------------- navigation */

    /**
     * Lets the browser do the validating. `reportValidity` puts the native
     * message on the offending field, which is better placed and better
     * announced than anything painted in here.
     */
    function stepIsValid(panel) {
      var fields = Array.prototype.slice.call(
        panel.querySelectorAll("input, select, textarea")
      );
      for (var i = 0; i < fields.length; i += 1) {
        if (!fields[i].checkValidity()) {
          fields[i].reportValidity();
          return false;
        }
      }
      return true;
    }

    function show(to) {
      index = Math.min(total - 1, Math.max(0, to));

      panels.forEach(function (panel, i) {
        panel.hidden = i !== index;
      });

      var last = index === total - 1;
      if (next) next.hidden = last;
      if (submit) submit.hidden = !last;
      if (back) back.hidden = index === 0;
      if (current) current.textContent = String(index + 1);
      if (bar) bar.style.width = ((index + 1) / total) * 100 + "%";

      if (last) buildSummary();

      form.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (next) {
      next.addEventListener("click", function () {
        if (!stepIsValid(panels[index])) return;
        syncServices();
        show(index + 1);
      });
    }

    if (back) {
      back.addEventListener("click", function () {
        show(index - 1);
      });
    }

    form.addEventListener("submit", function () {
      syncServices();
    });

    show(0);
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    initHeader();
    initMenu();
    initReveal();
    initServices();
    initAccordions();
    initComparisons();
    initCarousels();
    initWizard();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Shopify's theme editor re-renders sections in place. */
  document.addEventListener("shopify:section:load", boot);
})();
